import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Nullable } from '~types/Nullable';
import { downloadFile } from '~utils/utilFunctions';

type LightboxImage = {
  downloadUrl: Nullable<string>;
  fileName: string;
  imageUrl: string;
};

type ImageLightboxProps = {
  images: LightboxImage[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
};

export default function ImageLightbox({
  images,
  initialIndex,
  isOpen,
  onClose,
}: ImageLightboxProps) {
  const theme = useTheme();
  const [requestedIndex, setRequestedIndex] = useState(initialIndex);

  const shownIndex = Math.max(Math.min(requestedIndex, images.length - 1), 0);
  const shownImage = images[shownIndex];
  const hasSeveralImages = images.length > 1;
  const hasPrevious = shownIndex > 0;
  const hasNext = shownIndex < images.length - 1;

  const showPrevious = useCallback(() => {
    setRequestedIndex(Math.max(shownIndex - 1, 0));
  }, [shownIndex]);

  const showNext = useCallback(() => {
    setRequestedIndex(Math.min(shownIndex + 1, images.length - 1));
  }, [images.length, shownIndex]);

  useEffect(() => {
    if (!isOpen || !hasSeveralImages) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') showPrevious();
      if (event.key === 'ArrowRight') showNext();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasSeveralImages, isOpen, showNext, showPrevious]);

  const previousImageUrl = images[shownIndex - 1]?.imageUrl;
  const nextImageUrl = images[shownIndex + 1]?.imageUrl;

  useEffect(() => {
    [previousImageUrl, nextImageUrl].forEach((neighbourUrl) => {
      if (neighbourUrl) {
        new Image().src = neighbourUrl;
      }
    });
  }, [nextImageUrl, previousImageUrl]);

  const openOriginal = useCallback(() => {
    window.open(shownImage.imageUrl, '_blank', 'noopener,noreferrer');
  }, [shownImage]);

  // The file is stored off-origin, where the browser ignores the download attribute, so
  // only a url served as an attachment saves; without one the image opens in a new tab.
  const download = useCallback(() => {
    if (shownImage.downloadUrl) {
      downloadFile(shownImage.fileName, shownImage.downloadUrl);
    } else {
      downloadFile(shownImage.fileName, shownImage.imageUrl, true);
    }
  }, [shownImage]);

  return (
    <Dialog isOpen={isOpen} maxWidth="lg" onClose={onClose}>
      <DialogHeader onClose={onClose}>{shownImage.fileName}</DialogHeader>

      <DialogContent>
        <Box
          data-test-id="messaging-lightbox"
          sx={{ alignItems: 'center', display: 'flex', gap: 1, pt: 2 }}
        >
          {hasSeveralImages && (
            <IconButton
              aria-label={t('actions.previous')}
              data-test-id="messaging-lightbox-previous"
              disabled={!hasPrevious}
              onClick={showPrevious}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <Box
              alt={shownImage.fileName}
              component="img"
              src={shownImage.imageUrl}
              sx={{
                // The dialog is already clamped; keep the image inside it rather than
                // letting a tall photo push the footer off-screen.
                maxHeight: '70vh',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          {hasSeveralImages && (
            <IconButton
              aria-label={t('actions.next')}
              data-test-id="messaging-lightbox-next"
              disabled={!hasNext}
              onClick={showNext}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {hasSeveralImages && (
          <Typography
            data-test-id="messaging-lightbox-position"
            sx={{ color: theme.semantic.textSecondary, mr: 'auto' }}
            variant="body2"
          >
            {t('messaging.lightbox_position', {
              current: shownIndex + 1,
              total: images.length,
            })}
          </Typography>
        )}

        <SmallButton
          color="secondary"
          data-test-id="messaging-lightbox-open-original"
          onClick={openOriginal}
          startIcon={<OpenInNewOutlined sx={{ fontSize: 16 }} />}
          variant="outlined"
        >
          {t('messaging.open_original')}
        </SmallButton>

        <SmallButton
          color="brandV2Yellow"
          data-test-id="messaging-lightbox-download"
          onClick={download}
          startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
        >
          {t('actions.download')}
        </SmallButton>
      </DialogActions>
    </Dialog>
  );
}
